import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePermissoes } from '../../context/PermissoesContext';   // r100
import acessoService from '../../services/acesso.service';
import colaboradorService from '../../services/colaborador.service';
import clienteService from '../../services/cliente.service';
import ComboboxInline from '../../components/ComboboxInline';   // r-now: consulta rica (modelo do TarefaModal)
import { iconeDaRotina } from '../../constants/iconesRotinas';   // padroniza ícone (SVG) do cabeçalho
import {
  TIPOS_ACESSO, LABELS_ATRIBUTO, atributosDoTipo, ehSenha,
  VISIBILIDADES, VISIBILIDADE_PADRAO,
} from '../../constants/tiposAcesso';

// ════════════════════════════════════════════════════════════════════════════
// AcessoModal.jsx — r93
// ────────────────────────────────────────────────────────────────────────────
// Modal full-screen (95vw × 95vh) pra cadastrar/editar acessos.
//
// MODO 'novo':
//   - Cabeçalho: combo Usuário + combo Cliente
//   - Lista de "linhas de acesso" expansíveis: cada uma com Tipo + Descrição +
//     atributos dinâmicos (conforme tipo escolhido)
//   - Botão "+ Adicionar acesso" pra incluir mais linhas
//
// MODO 'editar':
//   - Cabeçalho readonly (Usuário e Cliente fixos do registro)
//   - Apenas 1 linha de acesso (o registro que está sendo editado)
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:  '#fff',    bg:      '#f0f2f7',
  verde:   '#1D9E75', verdeBg: '#f0fff4',
  vermelho:'#e53935', vermBg:  '#fff0f0',
  amarelo: '#d97706', amlBg:   '#fffbeb',
};

const inputStyle = {
  // r95: alinhado ao padrão do cadastro de Clientes
  width: '100%', height: '36px', padding: '0 10px',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  fontSize: '13px', boxSizing: 'border-box',
  backgroundColor: COR.branco, color: COR.texto, outline: 'none',
};

const selectStyle = { ...inputStyle, cursor: 'pointer' };

// Tag colorida do cliente (mesmo padrão das demais telas)
function TagCliente({ tag, cor, titulo }) {
  if (!tag) return null;
  const bgCor = cor || '#003399';
  const hex = bgCor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16) || 0;
  const g = parseInt(hex.substr(2, 2), 16) || 0;
  const b = parseInt(hex.substr(4, 2), 16) || 0;
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const corTexto = lum > 0.5 ? '#1a1a2e' : '#ffffff';
  return (
    <span title={titulo} style={{
      display: 'inline-block', padding: '2px 10px',
      borderRadius: '20px', fontSize: '11px', fontWeight: '500',
      backgroundColor: bgCor, color: corTexto,
      letterSpacing: '0.3px', fontFamily: "'Manrope', sans-serif",
    }}>{tag}</span>
  );
}

// r244: só Terminal Server tem Arquivo Rdp — um .rdp é atalho de Conexão de
// Área de Trabalho Remota, não faz sentido nos outros tipos de acesso.
const TIPO_COM_RDP = 'terminal-server';

// r243: ícone de download do Arquivo Rdp
function IconeDownload() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v12" />
      <path d="M7 11l5 5 5-5" />
      <path d="M4 20h16" />
    </svg>
  );
}

// r243: tamanho do .rdp em KB — são arquivos de poucos KB, MB seria sempre 0,0
function formatarKb(bytes) {
  const n = parseInt(bytes, 10) || 0;
  return `${(n / 1024).toFixed(1)} KB`;
}

function novaLinhaVazia(tipo = 'vpn') {
  // Inicializa atributos do tipo com valor vazio
  const atts = atributosDoTipo(tipo).map(ta => ({ tipo_attr: ta, valor: '' }));
  return {
    tipo_acesso: tipo, descricao: '', atributos: atts, _expandido: true,
    visibilidade: VISIBILIDADE_PADRAO,   // r245
    // r243: Arquivo Rdp — _rdpNome/_rdpTamanho é o que já está no servidor,
    // _rdpFile é o escolhido agora e _rdpRemover marca o apagar pendente.
    _rdpNome: null, _rdpTamanho: null, _rdpFile: null, _rdpRemover: false,
  };
}

function AcessoModal({ modo, acesso, colaboradorPreSel, clientePreSel, onFechar, onSalvou }) {
  const { usuario } = useAuth();
  const { filtroDaRotina, isAdmin } = usePermissoes();   // r100

  // r101: se a rotina 'acessos' tem filtro próprio 'meu_colaborador' no campo
  //       colaborador_id, força o combo a ser o próprio colaborador do usuário
  //       logado. O ID do colaborador é resolvido após carregar a lista de
  //       colaboradores (precisamos achar `colaborador.usuario_id == usuario.id`).
  const filtroAcessos = filtroDaRotina('acessos');
  const aplicarFiltroProprio = !isAdmin && filtroAcessos === 'meu_colaborador';

  // Cabeçalho — colaboradorId será atualizado após carregar a lista, se aplicarFiltroProprio
  const [colaboradorId, setColaboradorId] = useState(
    acesso?.colaborador_id || colaboradorPreSel || ''
  );
  const [colaboradorForcado, setColaboradorForcado] = useState(null);
  const [clienteId, setClienteId] = useState(acesso?.cliente_id || clientePreSel || '');

  // Listas pros combos
  const [colaboradoresLista, setColaboradoresLista] = useState([]);
  const [clientesLista, setClientesLista] = useState([]);

  // Linhas de acesso
  const [linhas, setLinhas] = useState(() => {
    if (modo === 'editar' && acesso) {
      // r245: completa com os atributos que o tipo ganhou depois que o registro
      // foi gravado (ex.: 'Outros' em Portal Cliente / App Chamados) — senão o
      // campo novo só apareceria trocando o tipo e voltando.
      const gravados = acesso.atributos || [];
      const faltantes = atributosDoTipo(acesso.tipo_acesso)
        .filter(ta => !gravados.some(a => a.tipo_attr === ta))
        .map(ta => ({ tipo_attr: ta, valor: '' }));
      return [{
        tipo_acesso: acesso.tipo_acesso,
        descricao:   acesso.descricao,
        atributos:   [...gravados, ...faltantes],
        visibilidade: acesso.visibilidade || VISIBILIDADE_PADRAO,   // r245
        _expandido:  true,
        // r243: o que já está gravado no servidor
        _rdpNome:    acesso.rdp_nome || null,
        _rdpTamanho: acesso.rdp_tamanho || null,
        _rdpFile:    null,
        _rdpRemover: false,
      }];
    }
    return [novaLinhaVazia()];
  });

  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  // Carregar usuários e clientes
  useEffect(() => {
    (async () => {
      try {
        const [cols, cs] = await Promise.all([
          colaboradorService.listar(),
          // r-now: /referencia (liberado a qualquer autenticado) — evita 403 pra
          //        quem não tem permissão do cadastro de Clientes. Usado só pra
          //        resolver o nome do cliente já selecionado (modo editar); a busca
          //        do combo bate direto em listarReferencia(termo).
          clienteService.listarReferencia(),
        ]);
        setColaboradoresLista(cols || []);
        setClientesLista(cs || []);
        // r101: resolve o colaborador correspondente ao usuário logado (se filtro próprio)
        if (aplicarFiltroProprio && usuario?.id) {
          const meuCol = (cols || []).find(c => c.usuario_id === usuario.id);
          if (meuCol) {
            setColaboradorForcado(meuCol.id);
            setColaboradorId(prev => prev || meuCol.id);
          }
        }
      } catch (err) {
        setErro('Erro ao carregar usuários/clientes: ' + (err.response?.data?.mensagem || err.message));
      }
    })();
  }, []);

  // Quando muda o tipo de acesso de uma linha, reinicializa os atributos preservando os valores existentes
  const trocarTipo = (idx, novoTipo) => {
    setLinhas(prev => prev.map((l, i) => {
      if (i !== idx) return l;
      const attsNovos = atributosDoTipo(novoTipo).map(ta => {
        // Mantém valor se o atributo já existia
        const existente = l.atributos.find(a => a.tipo_attr === ta);
        return { tipo_attr: ta, valor: existente?.valor || '' };
      });
      // r244: saindo de Terminal Server, o arquivo escolhido e ainda não
      // enviado é descartado — o campo vai desaparecer e não dá pra subir às
      // escondidas o que o usuário não vê mais. Arquivo JÁ gravado fica onde
      // está: apagar sozinho seria destrutivo, e volta a aparecer se o tipo
      // voltar pra Terminal Server (o browse segue oferecendo o download).
      const limpaRdp = novoTipo !== TIPO_COM_RDP
        ? { _rdpFile: null, _rdpRemover: false }
        : {};
      return { ...l, tipo_acesso: novoTipo, atributos: attsNovos, ...limpaRdp };
    }));
  };

  const trocarCampoLinha = (idx, campo, valor) => {
    setLinhas(prev => prev.map((l, i) => i === idx ? { ...l, [campo]: valor } : l));
  };

  const trocarAtributo = (idx, tipoAttr, valor) => {
    setLinhas(prev => prev.map((l, i) => {
      if (i !== idx) return l;
      return {
        ...l,
        atributos: l.atributos.map(a =>
          a.tipo_attr === tipoAttr ? { ...a, valor } : a
        ),
      };
    }));
  };

  const adicionarLinha = () => {
    setLinhas(prev => [...prev.map(l => ({ ...l, _expandido: false })), novaLinhaVazia()]);
  };

  const removerLinha = (idx) => {
    if (linhas.length === 1) {
      setErro('Não é possível remover a última linha.');
      return;
    }
    setLinhas(prev => prev.filter((_, i) => i !== idx));
  };

  // ── r243: Arquivo Rdp ───────────────────────────────────────────────────
  const escolherRdp = (idx, file) => {
    setErro('');
    if (!file.name.toLowerCase().endsWith('.rdp')) {
      return setErro('Selecione um arquivo .rdp.');
    }
    if (file.size > 1024 * 1024) {
      return setErro('O arquivo .rdp excede o limite de 1 MB.');
    }
    // Escolher um arquivo novo cancela a remoção pendente — o resultado final
    // é o arquivo novo, não "apaga e depois sobe".
    setLinhas(prev => prev.map((l, i) =>
      i === idx ? { ...l, _rdpFile: file, _rdpRemover: false } : l));
  };

  const removerRdp = (idx) => {
    setErro('');
    setLinhas(prev => prev.map((l, i) => {
      if (i !== idx) return l;
      // Arquivo só escolhido (nunca enviado): basta descartar da memória.
      if (l._rdpFile) return { ...l, _rdpFile: null };
      return { ...l, _rdpRemover: true };
    }));
  };

  const desfazerRemocaoRdp = (idx) => {
    setLinhas(prev => prev.map((l, i) => i === idx ? { ...l, _rdpRemover: false } : l));
  };

  const baixarRdp = async () => {
    if (!acesso?.id) return;
    try {
      await acessoService.baixarRdp(acesso.id, acesso.rdp_nome);
    } catch (err) {
      setErro('Erro ao baixar o Arquivo Rdp: ' + (err.response?.data?.mensagem || err.message));
    }
  };

  const toggleExpandir = (idx) => {
    setLinhas(prev => prev.map((l, i) =>
      i === idx ? { ...l, _expandido: !l._expandido } : l
    ));
  };

  const salvar = async () => {
    setErro('');
    if (!colaboradorId)  return setErro('Selecione o colaborador.');
    if (!clienteId)  return setErro('Selecione o cliente.');
    for (const l of linhas) {
      if (!l.tipo_acesso)         return setErro('Selecione o tipo em todos os acessos.');
      if (!l.descricao?.trim())   return setErro('Preencha a descrição em todos os acessos.');
    }

    try {
      setSalvando(true);
      if (modo === 'editar' && acesso?.id) {
        const l = linhas[0];
        await acessoService.alterar(acesso.id, {
          tipo_acesso: l.tipo_acesso,
          descricao:   l.descricao,
          visibilidade: l.visibilidade,   // r245
          atributos:   l.atributos.map(({ tipo_attr, valor }) => ({ tipo_attr, valor })),
        });
        // r243: o Arquivo Rdp vai em request própria (multipart) depois do PUT
        // r244: só pra Terminal Server — nos outros tipos o campo não aparece,
        //       então não há intenção do usuário pra executar aqui.
        if (l.tipo_acesso === TIPO_COM_RDP) {
          if (l._rdpFile)         await acessoService.uploadRdp(acesso.id, l._rdpFile);
          else if (l._rdpRemover) await acessoService.excluirRdp(acesso.id);
        }
      } else {
        const resp = await acessoService.criar({
          colaborador_id: colaboradorId,
          cliente_id: clienteId,
          acessos: linhas.map(l => ({
            tipo_acesso: l.tipo_acesso,
            descricao:   l.descricao,
            visibilidade: l.visibilidade,   // r245
            atributos:   l.atributos.map(({ tipo_attr, valor }) => ({ tipo_attr, valor })),
          })),
        });
        // r243: o backend devolve os criados na MESMA ordem do array enviado,
        // então o índice da linha é o índice do id recém-criado.
        const criados = resp?.criados || [];
        for (let i = 0; i < linhas.length; i++) {
          if (linhas[i].tipo_acesso !== TIPO_COM_RDP) continue;   // r244
          const file = linhas[i]._rdpFile;
          const novo = criados[i];
          if (file && novo?.id) await acessoService.uploadRdp(novo.id, file);
        }
      }
      if (onSalvou) onSalvou();
      onFechar();
    } catch (err) {
      setErro('Erro ao salvar: ' + (err.response?.data?.mensagem || err.message));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1400, fontFamily: "'Manrope', sans-serif",
      // r243: clique fora NÃO fecha — só os botões Cancelar, Salvar e ×. Era
      // fácil perder o formulário todo num clique distraído na borda.
    }}>
      <div style={{
        backgroundColor: COR.branco, borderRadius: '8px',
        width: '95vw', height: '95vh',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header — r-now: inclui botões Cancelar/Salvar à direita (saíram do footer) */}
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${COR.borda}`,
          backgroundColor: COR.azul, color: COR.branco,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderTopLeftRadius: '8px', borderTopRightRadius: '8px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
            {/* Ícone SVG da rotina — mesmo padrão do Modal rico (36x36, à esquerda) */}
            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              {iconeDaRotina('acessos', { width: 36, height: 36, color: COR.branco })}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '16px', fontWeight: '700' }}>
                {modo === 'editar' ? 'Editar Acesso' : 'Cadastro de Acessos'}
              </div>
              <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '2px' }}>
                {modo === 'editar'
                  ? `Registro ${acesso?.id} · Seq ${acesso?.seq}`
                  : 'Selecione usuário e cliente, depois adicione os acessos necessários.'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={onFechar} style={{
              padding: '8px 18px', fontSize: '12px', fontWeight: '600',
              backgroundColor: 'transparent', color: COR.branco,
              border: `1px solid rgba(255,255,255,0.5)`, borderRadius: '4px',
              cursor: 'pointer',
            }}>
              Cancelar
            </button>
            <button onClick={salvar} disabled={salvando} style={{
              padding: '8px 22px', fontSize: '12px', fontWeight: '700',
              backgroundColor: COR.branco, color: COR.azul,
              border: 'none', borderRadius: '4px',
              cursor: salvando ? 'wait' : 'pointer',
              opacity: salvando ? 0.6 : 1,
            }}>
              {salvando ? 'Salvando...' : '✓ Salvar'}
            </button>
            <button onClick={onFechar} title="Fechar" style={{
              background: 'transparent', border: 'none',
              color: COR.branco, fontSize: '24px', cursor: 'pointer',
              padding: '0 8px', marginLeft: '4px',
            }}>×</button>
          </div>
        </div>

        {/* Erro */}
        {erro && (
          <div style={{
            padding: '10px 20px', backgroundColor: COR.vermBg,
            color: COR.vermelho, fontSize: '13px', fontWeight: '600',
          }}>{erro}</div>
        )}

        {/* Conteúdo */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', backgroundColor: COR.bg }}>

          {/* Cabeçalho: Usuário + Cliente */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px',
            backgroundColor: COR.branco, padding: '12px',
            border: `1px solid ${COR.borda}`, borderRadius: '6px',
            marginBottom: '16px',
          }}>
            <div>
              <label style={{ fontSize: '11px', color: COR.muted, fontWeight: '600' }}>
                Colaborador
                {colaboradorForcado && (
                  <span style={{ marginLeft: '6px', fontWeight: '400', color: COR.muted }}>
                    (apenas seus próprios acessos)
                  </span>
                )}
              </label>
              <select
                value={colaboradorId}
                onChange={e => setColaboradorId(e.target.value)}
                disabled={modo === 'editar' || !!colaboradorForcado}
                style={{
                  ...selectStyle, marginTop: '4px',
                  cursor: (modo === 'editar' || colaboradorForcado) ? 'not-allowed' : 'pointer',
                }}
              >
                <option value="">— Selecione —</option>
                {colaboradoresLista
                  .filter(c => c.status === 'A')
                  // r101: se há filtro próprio, só mostra o próprio colaborador no combo
                  .filter(c => !colaboradorForcado || c.id === colaboradorForcado)
                  .map(c => (
                    <option key={c.id} value={c.id}>{c.nome_amigavel || c.nome}</option>
                  ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: COR.muted, fontWeight: '600' }}>Cliente</label>
              <div style={{ marginTop: '4px' }}>
                {(() => {
                  const cliSel = clientesLista.find(c => c.id === clienteId) || null;
                  return (
                    <ComboboxInline
                      valor={clienteId}
                      labelAtual={cliSel ? (cliSel.nome_fantasia || cliSel.razao_social) : clienteId}
                      rawAtual={cliSel}
                      renderSelecionado={(c) => (
                        <>
                          <TagCliente tag={c?.tag} cor={c?.cor_tag} titulo={c?.nome_fantasia} />
                          <span style={{ fontSize: '13px', color: COR.texto, flex: 1 }}>
                            {c?.nome_fantasia || c?.razao_social}
                          </span>
                        </>
                      )}
                      buscar={(termo) => clienteService.listarReferencia(termo)}
                      getId={(c) => c.id}
                      getLabel={(c) => c.nome_fantasia || c.razao_social}
                      renderItem={(c) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <TagCliente tag={c.tag} cor={c.cor_tag} titulo={c.nome_fantasia} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: COR.texto }}>
                              {c.nome_fantasia || c.razao_social}
                            </div>
                            {c.razao_social && c.nome_fantasia && c.razao_social !== c.nome_fantasia && (
                              <div style={{ fontSize: '10px', color: COR.muted, marginTop: '2px' }}>
                                {c.razao_social}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      onSelecionar={(sel) => setClienteId(sel?.id || '')}
                      placeholder="Nome fantasia, razão social ou CNPJ..."
                      desabilitado={modo === 'editar'}
                    />
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Linhas de acesso */}
          {linhas.map((l, idx) => (
            <LinhaAcesso
              key={idx}
              linha={l}
              indice={idx}
              total={linhas.length}
              modo={modo}
              onToggle={() => toggleExpandir(idx)}
              onTrocarTipo={v => trocarTipo(idx, v)}
              onTrocarCampo={(c, v) => trocarCampoLinha(idx, c, v)}
              onTrocarAttr={(ta, v) => trocarAtributo(idx, ta, v)}
              onRemover={() => removerLinha(idx)}
              onEscolherRdp={f => escolherRdp(idx, f)}
              onRemoverRdp={() => removerRdp(idx)}
              onDesfazerRemocaoRdp={() => desfazerRemocaoRdp(idx)}
              onBaixarRdp={baixarRdp}
            />
          ))}

          {/* Botão adicionar nova linha (só em modo novo) */}
          {modo !== 'editar' && (
            <button onClick={adicionarLinha} style={{
              marginTop: '8px', width: '100%',
              padding: '10px', fontSize: '13px', fontWeight: '600',
              border: `2px dashed ${COR.azul}`, borderRadius: '6px',
              backgroundColor: 'transparent', color: COR.azul,
              cursor: 'pointer',
            }}>
              + Adicionar acesso
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sub-componente: uma linha de acesso ────────────────────────────────────
function LinhaAcesso({ linha, indice, total, modo, onToggle, onTrocarTipo,
                       onTrocarCampo, onTrocarAttr, onRemover,
                       onEscolherRdp, onRemoverRdp, onDesfazerRemocaoRdp, onBaixarRdp }) {
  const [senhasVisiveis, setSenhasVisiveis] = useState({});

  const tipoLabel = TIPOS_ACESSO.find(t => t.codigo === linha.tipo_acesso)?.label || linha.tipo_acesso;
  const temRdpSalvo = !!linha._rdpNome;   // r243: já existe arquivo no servidor
  const ehTerminalServer = linha.tipo_acesso === TIPO_COM_RDP;   // r244

  return (
    <div style={{
      backgroundColor: COR.branco, border: `1px solid ${COR.borda}`,
      borderRadius: '6px', marginBottom: '8px', overflow: 'hidden',
    }}>
      {/* Cabeçalho clicável */}
      <div onClick={onToggle} style={{
        padding: '10px 14px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        backgroundColor: linha._expandido ? COR.azulBg : COR.branco,
        borderBottom: linha._expandido ? `1px solid ${COR.borda}` : 'none',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '12px', color: COR.muted }}>
            {linha._expandido ? '▼' : '▶'}
          </span>
          <span style={{ fontSize: '13px', fontWeight: '700', color: COR.texto }}>
            Acesso #{indice + 1} · {tipoLabel}
          </span>
          {linha.descricao && (
            <span style={{ fontSize: '12px', color: COR.muted }}>
              — {linha.descricao}
            </span>
          )}
        </div>
        {modo !== 'editar' && total > 1 && (
          <button onClick={e => { e.stopPropagation(); onRemover(); }} title="Remover" style={{
            border: 'none', background: 'transparent', color: COR.vermelho,
            cursor: 'pointer', fontSize: '14px', padding: '2px 6px',
          }}>🗑 Remover</button>
        )}
      </div>

      {/* Corpo expandido */}
      {linha._expandido && (
        <div style={{ padding: '14px' }}>
          {/* Tipo + Descrição */}
          <div style={{
            // r245: + Visibilidade (180px) à direita da descrição
            display: 'grid', gridTemplateColumns: '200px 1fr 180px', gap: '10px',
            marginBottom: '14px',
          }}>
            <div>
              <label style={{ fontSize: '11px', color: COR.muted, fontWeight: '600' }}>Tipo de Acesso</label>
              <select
                value={linha.tipo_acesso}
                onChange={e => onTrocarTipo(e.target.value)}
                style={{ ...selectStyle, marginTop: '4px' }}
              >
                {TIPOS_ACESSO.map(t => (
                  <option key={t.codigo} value={t.codigo}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: COR.muted, fontWeight: '600' }}>Descrição</label>
              <input
                type="text"
                value={linha.descricao}
                onChange={e => onTrocarCampo('descricao', e.target.value)}
                placeholder="Ex: Ambiente de Produção"
                style={{ ...inputStyle, marginTop: '4px' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: COR.muted, fontWeight: '600' }}
                     title="Restrito: só você e os administradores visualizam/editam este acesso">
                Visibilidade
              </label>
              <select
                value={linha.visibilidade || VISIBILIDADE_PADRAO}
                onChange={e => onTrocarCampo('visibilidade', e.target.value)}
                style={{
                  ...selectStyle, marginTop: '4px',
                  color: (VISIBILIDADES[linha.visibilidade] || VISIBILIDADES[VISIBILIDADE_PADRAO]).cor,
                  fontWeight: '600',
                }}
              >
                {Object.entries(VISIBILIDADES).map(([v, o]) => (
                  <option key={v} value={v}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          {linha.visibilidade === 'R' && (
            <div style={{ fontSize: '11px', color: COR.vermelho, marginTop: '-8px', marginBottom: '14px' }}>
              Restrito: somente você e os usuários administradores poderão visualizar e editar este acesso.
            </div>
          )}


          {/* r243: Arquivo Rdp — um .rdp por acesso. No modo novo o arquivo
              fica em memória e sobe depois do POST (o acesso ainda não tem id).
              Em edição sobe junto com o Salvar, pra o botão Cancelar realmente
              desfazer o que foi mexido aqui.
              r244: exclusivo do tipo Terminal Server. */}
          {ehTerminalServer && (
          <div style={{
            borderTop: `1px solid ${COR.borda}`, paddingTop: '12px',
            marginBottom: '14px',
          }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '130px 1fr',
              alignItems: 'center', gap: '12px',
            }}>
              <label style={{
                fontSize: '12px', color: COR.muted, fontWeight: '600',
                textAlign: 'right',
              }}>
                Arquivo Rdp
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                {/* Arquivo já gravado no servidor — some quando marcado pra remover */}
                {temRdpSalvo && !linha._rdpRemover && !linha._rdpFile && (
                  <>
                    <button type="button" onClick={onBaixarRdp} style={{
                      padding: '0 12px', height: '36px', fontSize: '12px', fontWeight: '600',
                      border: `1px solid ${COR.azul}`, borderRadius: '5px',
                      backgroundColor: COR.azulBg, color: COR.azul, cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                    }}>
                      <IconeDownload /> Baixar
                    </button>
                    <span style={{ fontSize: '12px', color: COR.texto }}>
                      {linha._rdpNome}
                      {linha._rdpTamanho ? (
                        <span style={{ color: COR.muted }}> · {formatarKb(linha._rdpTamanho)}</span>
                      ) : null}
                    </span>
                  </>
                )}

                {/* Arquivo recém-escolhido, ainda não enviado */}
                {linha._rdpFile && (
                  <span style={{ fontSize: '12px', color: COR.verde, fontWeight: '600' }}>
                    {linha._rdpFile.name}
                    <span style={{ color: COR.muted, fontWeight: '400' }}>
                      {' '}· {formatarKb(linha._rdpFile.size)} · será enviado ao salvar
                    </span>
                  </span>
                )}

                {linha._rdpRemover && !linha._rdpFile && (
                  <span style={{ fontSize: '12px', color: COR.vermelho, fontWeight: '600' }}>
                    Será removido ao salvar
                  </span>
                )}

                {!temRdpSalvo && !linha._rdpFile && (
                  <span style={{ fontSize: '12px', color: COR.muted, fontStyle: 'italic' }}>
                    Nenhum arquivo
                  </span>
                )}

                <label style={{
                  padding: '0 12px', height: '36px', fontSize: '12px', fontWeight: '600',
                  border: `1px solid ${COR.borda}`, borderRadius: '5px',
                  backgroundColor: COR.branco, color: COR.texto, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center',
                }}>
                  {temRdpSalvo || linha._rdpFile ? 'Trocar...' : 'Selecionar...'}
                  <input
                    type="file" accept=".rdp"
                    onChange={e => {
                      const f = e.target.files?.[0] || null;
                      e.target.value = '';   // permite reescolher o MESMO arquivo
                      if (f) onEscolherRdp(f);
                    }}
                    style={{ display: 'none' }}
                  />
                </label>

                {(temRdpSalvo || linha._rdpFile) && !linha._rdpRemover && (
                  <button type="button" onClick={onRemoverRdp} title="Remover o arquivo" style={{
                    padding: '0 12px', height: '36px', fontSize: '12px', fontWeight: '600',
                    border: `1px solid ${COR.borda}`, borderRadius: '5px',
                    backgroundColor: COR.branco, color: COR.vermelho, cursor: 'pointer',
                  }}>
                    Remover
                  </button>
                )}
                {linha._rdpRemover && (
                  <button type="button" onClick={onDesfazerRemocaoRdp} style={{
                    padding: '0 12px', height: '36px', fontSize: '12px', fontWeight: '600',
                    border: `1px solid ${COR.borda}`, borderRadius: '5px',
                    backgroundColor: COR.branco, color: COR.texto, cursor: 'pointer',
                  }}>
                    Desfazer
                  </button>
                )}
              </div>
            </div>
            <div style={{
              fontSize: '11px', color: COR.muted, marginTop: '6px',
              marginLeft: '142px',
            }}>
              Só arquivos .rdp, até 1 MB.
            </div>
          </div>
          )}

          {/* Atributos dinâmicos */}
          {linha.atributos.length > 0 && (
            <div style={{
              borderTop: `1px solid ${COR.borda}`, paddingTop: '12px',
            }}>
              <div style={{ fontSize: '11px', color: COR.muted, fontWeight: '600', marginBottom: '8px' }}>
                Atributos
              </div>
              {/* r96: 1 coluna — cada atributo numa linha, label à esquerda + input ocupando o resto */}
              <div style={{
                display: 'flex', flexDirection: 'column', gap: '8px',
              }}>
                {linha.atributos.map(att => {
                  const isSenha = ehSenha(att.tipo_attr);
                  const visivel = senhasVisiveis[att.tipo_attr];
                  return (
                    <div key={att.tipo_attr} style={{
                      display: 'grid',
                      gridTemplateColumns: '130px 1fr',
                      alignItems: 'center', gap: '12px',
                    }}>
                      <label style={{
                        fontSize: '12px', color: COR.muted, fontWeight: '600',
                        textAlign: 'right',
                      }}>
                        {LABELS_ATRIBUTO[att.tipo_attr] || att.tipo_attr}
                      </label>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <input
                          type={isSenha && !visivel ? 'password' : 'text'}
                          value={att.valor || ''}
                          onChange={e => onTrocarAttr(att.tipo_attr, e.target.value)}
                          placeholder={LABELS_ATRIBUTO[att.tipo_attr]}
                          style={inputStyle}
                        />
                        {isSenha && (
                          <button
                            type="button"
                            onClick={() => setSenhasVisiveis(s => ({
                              ...s, [att.tipo_attr]: !s[att.tipo_attr],
                            }))}
                            title={visivel ? 'Ocultar' : 'Mostrar'}
                            style={{
                              padding: '0 12px', fontSize: '13px',
                              border: `1px solid ${COR.borda}`, borderRadius: '5px',
                              backgroundColor: COR.branco, cursor: 'pointer',
                              flexShrink: 0,
                            }}>
                            {visivel ? '🙈' : '👁'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AcessoModal;
